// Webhook configuration
const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL;
const ABANDON_CART_WEBHOOK_URL =
  process.env.REACT_APP_ABANDONED_CART_WEBHOOK_URL;

// Calculation cache to avoid recomputing expensive operations
const calculationCache = new Map();
const MAX_CACHE_SIZE = 50;

/**
 * Memoization utility for expensive calculations
 * @param {string} cacheKey - Unique key for the calculation
 * @param {Function} calculator - The calculation function
 * @param {*} args - Arguments for the calculation
 * @returns {*} - Cached or newly calculated result
 */
const getMemoizedCalculation = (cacheKey, calculator, ...args) => {
  // Create a unique key from the cache key and arguments
  const fullKey = `${cacheKey}-${JSON.stringify(args)}`;

  // Check if result is already cached
  if (calculationCache.has(fullKey)) {
    return calculationCache.get(fullKey);
  }

  // Calculate new result
  const result = calculator(...args);

  // Cache the result (with size limit)
  if (calculationCache.size >= MAX_CACHE_SIZE) {
    const firstKey = calculationCache.keys().next().value;
    calculationCache.delete(firstKey);
  }
  calculationCache.set(fullKey, result);

  return result;
};

/**
 * Clear calculation cache (useful for testing or memory management)
 */
export const clearCalculationCache = () => {
  calculationCache.clear();
};

/**
 * Generates a unique session ID for tracking user journey
 * @returns {string} - Unique session identifier
 */
export const generateSessionId = () => {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `session_${timestamp}_${randomStr}`;
};

/**
 * Sends abandon cart data to the n8n webhook (Step 4 trigger)
 * @param {Object} abandonData - The abandon cart data object
 * @returns {Promise} - Promise that resolves with the webhook response
 */
export const sendAbandonCartData = async (abandonData) => {
  try {
    if (!ABANDON_CART_WEBHOOK_URL) {
      return { success: false, error: "Webhook URL not configured" };
    }

    const payload = {
      timestamp: new Date().toISOString(),
      source: "AI Business Lead Capture Form - Abandon Cart",
      trigger_point: "step_4_business_info",
      completion_percentage: 25,

      // Session tracking
      session_id: abandonData.sessionId,

      // Contact information (captured at step 4)
      contact_info: {
        first_name: abandonData.firstName,
        last_name: abandonData.lastName,
        email: abandonData.email,
      },

      // Business information (step 4)
      business_info: {
        business_name: abandonData.businessName,
        business_type: abandonData.businessType,
        years_in_business: abandonData.yearsInBusiness,
        employee_count: abandonData.employeeCount,
        current_revenue: abandonData.currentRevenue,
      },

      // COMPLETE Step 1-4 data capture
      step_1_strategic_insights: {
        biggest_growth_challenge: abandonData.biggestGrowthChallenge,
        custom_growth_challenge: abandonData.customGrowthChallenge,
      },

      step_2_time_management: {
        most_time_consuming_task: abandonData.timeConsumingTasks,
      },

      step_3_lead_flow: {
        lead_sources: abandonData.leadSources || [],
        leads_per_week: abandonData.leadsPerWeek,
        books_appointments: abandonData.booksAppointments,
        no_show_rate: abandonData.noShowRate,
        tracks_missed_calls: abandonData.tracksMissedCalls,
        after_hours_enquiries: abandonData.afterHoursEnquiries,
        after_hours_share: abandonData.afterHoursShare,
        after_hours_respond: abandonData.afterHoursRespond,
      },

      // Quick summary for email personalization
      quick_summary: {
        full_name: `${abandonData.firstName} ${abandonData.lastName}`,
        business_name: abandonData.businessName,
        business_type: abandonData.businessType,
        primary_challenge: abandonData.biggestGrowthChallenge,
        weekly_leads: abandonData.leadsPerWeek,
        time_challenge: abandonData.timeConsumingTasks,
        company_size: abandonData.employeeCount,
        experience_level: abandonData.yearsInBusiness,
        revenue_level: abandonData.currentRevenue,
      },

      // Abandon cart metadata
      abandon_cart_metadata: {
        steps_completed: 4,
        total_steps: 15,
        estimated_time_invested: "3-5 minutes",
        data_quality_score: calculateDataQualityScore(abandonData),
        completion_percentage: 25,
        next_steps_remaining: 11,
        data_completeness: {
          contact_info: !!(
            abandonData.firstName &&
            abandonData.lastName &&
            abandonData.email
          ),
          business_info: !!(
            abandonData.businessName && abandonData.businessType
          ),
          strategic_insights: !!abandonData.biggestGrowthChallenge,
          time_management: !!abandonData.timeConsumingTasks,
          lead_flow: !!(
            abandonData.leadSources && abandonData.leadSources.length > 0
          ),
        },
      },
    };

    const response = await fetch(ABANDON_CART_WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Abandon cart webhook request failed with status ${response.status}`
      );
    }

    const result = await response.json();
    return { success: true, data: result };
  } catch (error) {
    console.error("Error sending abandon cart data:", error);
    // Don't throw error - we don't want to block user progression
    return { success: false, error: error.message };
  }
};

/**
 * Calculates data quality score for abandon cart leads
 * @param {Object} data - The form data
 * @returns {number} - Quality score from 1-100
 */
const calculateDataQualityScore = (data) => {
  let score = 0;

  // Contact completeness (25 points)
  if (data.firstName) score += 10;
  if (data.lastName) score += 10;
  if (data.email && data.email.includes("@")) score += 5;

  // Business info completeness (25 points)
  if (data.businessName) score += 8;
  if (data.businessType) score += 8;
  if (data.yearsInBusiness) score += 5;
  if (data.employeeCount) score += 4;

  // Step 1: Strategic insights (20 points)
  if (data.biggestGrowthChallenge) score += 15;
  if (data.customGrowthChallenge) score += 5;

  // Step 2: Time management (10 points)
  if (data.timeConsumingTasks) score += 10;

  // Step 3: Lead flow (20 points)
  if (data.leadSources && data.leadSources.length > 0) score += 8;
  if (data.leadsPerWeek) score += 5;
  if (data.afterHoursEnquiries) score += 4;
  if (data.afterHoursShare) score += 3;

  return Math.min(100, score);
};

/**
 * Sends form data to the n8n webhook
 * @param {Object} formData - The complete form data object
 * @returns {Promise} - Promise that resolves with the webhook response
 */
export const sendToWebhook = async (formData) => {
  try {
    if (!WEBHOOK_URL) {
      return { success: false, error: "Webhook URL not configured" };
    }

    // 🚀 5-AGENT OPTIMIZED PAYLOAD STRUCTURE
    const payload = {
      // Session metadata for tracking
      session_metadata: {
        timestamp: new Date().toISOString(),
        source: "AI Business Lead Capture Form - Final Submission",
        session_id: formData.sessionId,
        total_steps_completed: formData.userPath?.length || 0,
        form_journey: {
          completed_steps: formData.completedSteps,
          user_path: formData.userPath,
        },
      },

      // 🕵️ AGENT 1: REVENUE LEAK DETECTIVE
      // Focus: Lead flow, conversion rates, missed opportunities
      agent_1_revenue_leak_detective: {
        lead_generation_data: {
          lead_sources: formData.leadSources || [],
          leads_per_week: formData.leadsPerWeek,
          lead_response_time: formData.leadResponse,
          tracks_missed_calls: formData.tracksMissedCalls,
          missed_opportunities: formData.missedOpportunities,
        },
        conversion_killers: {
          books_appointments: formData.booksAppointments,
          no_show_rate: formData.noShowRate,
          lost_client_due_to_slow_response:
            formData.lostClientDueToSlowResponse,
          lead_source_followup: formData.leadSourceFollowUp,
        },
        after_hours_opportunity: {
          after_hours_enquiries: formData.afterHoursEnquiries,
          after_hours_share: formData.afterHoursShare,
          after_hours_respond: formData.afterHoursRespond,
        },
        revenue_impact: {
          extra_client_value: formData.extraClientValue,
          missed_lead_cost: formData.missedLeadCost,
          current_revenue: formData.currentRevenue,
        },
      },

      // 🎯 AGENT 2: COMPETITIVE INTELLIGENCE
      // Focus: Market position, readiness scores, automation gaps
      agent_2_competitive_intelligence: {
        business_foundation: {
          business_type: formData.businessType,
          years_in_business: formData.yearsInBusiness,
          employee_count: formData.employeeCount,
          team_structure: formData.teamStructure,
        },
        current_tech_stack: {
          current_tools: formData.currentTools,
          current_tooling_shortcuts: formData.currentToolingShortcuts || [],
          lead_sources: formData.leadSources || [],
        },
        market_position_data: {
          lead_response_time: formData.leadResponse,
          automation_preferences: formData.automationPreferences || [],
          biggest_growth_challenge: formData.biggestGrowthChallenge,
        },
      },

      // ⚙️ AGENT 3: OPERATIONS OPTIMIZER
      // Focus: Time management, current tools, efficiency gaps
      agent_3_operations_optimizer: {
        time_drains: {
          most_time_consuming_task: formData.timeConsumingTasks,
          lead_response_time: formData.leadResponse,
          missed_opportunities: formData.missedOpportunities,
        },
        current_systems: {
          current_tools: formData.currentTools,
          current_tooling_shortcuts: formData.currentToolingShortcuts || [],
          tracks_missed_calls: formData.tracksMissedCalls,
          past_client_system: formData.pastClientSystem,
        },
        automation_appetite: {
          automation_preferences: formData.automationPreferences || [],
          content_creation_method: formData.contentCreationMethod,
          team_structure: formData.teamStructure,
        },
        efficiency_metrics: {
          leads_per_week: formData.leadsPerWeek,
          books_appointments: formData.booksAppointments,
          no_show_rate: formData.noShowRate,
        },
      },

      // 🚀 AGENT 4: GROWTH STRATEGIST
      // Focus: Goals, preferences, implementation roadmap
      agent_4_growth_strategist: {
        strategic_goals: {
          primary_goal: formData.primaryGoal,
          secondary_goals: formData.secondaryGoals || [],
          biggest_growth_challenge: formData.biggestGrowthChallenge,
          custom_growth_challenge: formData.customGrowthChallenge,
        },
        automation_roadmap: {
          automation_preferences: formData.automationPreferences || [],
          content_creation_method: formData.contentCreationMethod,
          lead_source_followup: formData.leadSourceFollowUp,
        },
        business_context: {
          business_type: formData.businessType,
          years_in_business: formData.yearsInBusiness,
          employee_count: formData.employeeCount,
          current_revenue: formData.currentRevenue,
          team_structure: formData.teamStructure,
        },
        growth_metrics: {
          leads_per_week: formData.leadsPerWeek,
          extra_client_value: formData.extraClientValue,
          lead_sources: formData.leadSources || [],
        },
      },

      // 🧠 AGENT 5: PSYCHOLOGY TRIGGER MASTER
      // Focus: Pain points, motivations, buying signals
      agent_5_psychology_trigger_master: {
        pain_points: {
          biggest_growth_challenge: formData.biggestGrowthChallenge,
          most_time_consuming_task: formData.timeConsumingTasks,
          lost_client_due_to_slow_response:
            formData.lostClientDueToSlowResponse,
          missed_opportunities: formData.missedOpportunities,
          lead_response_time: formData.leadResponse,
        },
        buying_signals: {
          automation_preferences: formData.automationPreferences || [],
          primary_goal: formData.primaryGoal,
          secondary_goals: formData.secondaryGoals || [],
          additional_info: formData.additionalInfo,
        },
        urgency_indicators: {
          no_show_rate: formData.noShowRate,
          missed_lead_cost: formData.missedLeadCost,
          after_hours_enquiries: formData.afterHoursEnquiries,
          current_revenue: formData.currentRevenue,
        },
        personalization_data: {
          business_name: formData.businessName,
          business_type: formData.businessType,
          years_in_business: formData.yearsInBusiness,
          employee_count: formData.employeeCount,
        },
      },

      // 📊 CALCULATED INSIGHTS & INTELLIGENCE
      // All computed scores and analyses for agent reference (with memoization)
      calculated_insights: {
        scores: {
          lead_score: getMemoizedCalculation(
            "lead_score",
            calculateLeadScore,
            formData
          ),
          urgency_level: getMemoizedCalculation(
            "urgency_level",
            calculateUrgencyLevel,
            formData
          ),
          automation_priority: getMemoizedCalculation(
            "automation_priority",
            calculateAutomationPriority,
            formData
          ),
        },
        assessments: {
          business_maturity_assessment: getMemoizedCalculation(
            "business_maturity",
            calculateBusinessMaturity,
            formData
          ),
          technology_adoption_score: getMemoizedCalculation(
            "tech_adoption",
            calculateTechnologyAdoption,
            formData
          ),
          pain_point_analysis: getMemoizedCalculation(
            "pain_points",
            calculatePainPointSeverity,
            formData
          ),
          automation_readiness_score: getMemoizedCalculation(
            "automation_readiness",
            calculateAutomationReadiness,
            formData
          ),
          customer_journey_complexity: getMemoizedCalculation(
            "journey_complexity",
            calculateCustomerJourneyComplexity,
            formData
          ),
          growth_potential_score: getMemoizedCalculation(
            "growth_potential",
            calculateGrowthPotential,
            formData
          ),
          digital_transformation_readiness: getMemoizedCalculation(
            "digital_readiness",
            calculateDigitalReadiness,
            formData
          ),
        },
        financial_analysis: {
          revenue_opportunity: getMemoizedCalculation(
            "revenue_opportunity",
            calculateRevenueOpportunity,
            formData
          ),
          revenue_leak_analysis: getMemoizedCalculation(
            "revenue_leak",
            calculateRevenueLeak,
            formData
          ),
          risk_assessment: getMemoizedCalculation(
            "risk_assessment",
            calculateRiskAssessment,
            formData
          ),
        },
        competitive_analysis: {
          lead_source_analysis: getMemoizedCalculation(
            "lead_sources",
            analyzeLeadSources,
            formData
          ),
          team_efficiency_analysis: getMemoizedCalculation(
            "team_efficiency",
            analyzeTeamEfficiency,
            formData
          ),
          competitive_analysis: getMemoizedCalculation(
            "competitive_position",
            calculateCompetitivePosition,
            formData
          ),
        },
        implementation: {
          implementation_recommendations: getMemoizedCalculation(
            "implementation",
            generateImplementationRecommendations,
            formData
          ),
        },
      },

      // 📞 CONTACT INFORMATION
      // For personalization across all agents
      contact_info: {
        first_name: formData.firstName,
        last_name: formData.lastName,
        full_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        business_name: formData.businessName,
        additional_info: formData.additionalInfo,
      },
    };

    // Send the data to the webhook

    const response = await fetch(WEBHOOK_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Webhook request failed with status ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    // Re-throw the error to be handled by the calling component
    throw error;
  }
};

/**
 * Calculates a lead score based on form responses
 * @param {Object} formData - The form data
 * @returns {number} - Lead score from 1-100
 */
const calculateLeadScore = (formData) => {
  let score = 0;

  // Business maturity (years in business)
  const yearsMap = {
    "Less than 1 year": 5,
    "1-2 years": 15,
    "3-5 years": 25,
    "6-10 years": 35,
    "11-20 years": 30,
    "More than 20 years": 25,
  };
  score += yearsMap[formData.yearsInBusiness] || 0;

  // Business size (employee count)
  const employeeMap = {
    "Just me (1)": 10,
    "2-5 employees": 20,
    "6-10 employees": 25,
    "11-25 employees": 30,
    "26-50 employees": 25,
    "More than 50 employees": 20,
  };
  score += employeeMap[formData.employeeCount] || 0;

  // Lead response urgency
  const responseMap = {
    immediate: 25,
    "few-hours": 20,
    "day-or-more": 10,
    "forget-unless-message": 5,
    automation: 30,
  };
  score += responseMap[formData.leadResponse] || 0;

  // Automation readiness (number of automation preferences)
  const automationCount = (formData.automationPreferences || []).length;
  score += Math.min(20, automationCount * 3);

  return Math.min(100, Math.max(1, score));
};

/**
 * Calculates urgency level based on current challenges and missed opportunities
 * @param {Object} formData - The form data
 * @returns {string} - Urgency level: 'high', 'medium', or 'low'
 */
const calculateUrgencyLevel = (formData) => {
  // High urgency indicators
  if (
    formData.leadResponse === "forget-unless-message" ||
    formData.missedOpportunities === "often" ||
    formData.lostClientDueToSlowResponse === "definitely"
  ) {
    return "high";
  }

  // Medium urgency indicators
  if (
    formData.leadResponse === "day-or-more" ||
    formData.missedOpportunities === "sometimes" ||
    formData.lostClientDueToSlowResponse === "probably" ||
    (formData.automationPreferences || []).length > 3
  ) {
    return "medium";
  }

  return "low";
};

/**
 * Calculates automation priority based on selected preferences and challenges
 * @param {Object} formData - The form data
 * @returns {string} - Priority level: 'high', 'medium', or 'low'
 */
const calculateAutomationPriority = (formData) => {
  const highPriorityPreferences = [
    "lead-followup",
    "quotes-invoices",
    "appointment-confirmations",
  ];
  const selectedPreferences = formData.automationPreferences || [];

  const hasHighPriority = selectedPreferences.some((pref) =>
    highPriorityPreferences.includes(pref)
  );

  if (hasHighPriority && selectedPreferences.length >= 3) {
    return "high";
  } else if (selectedPreferences.length >= 2) {
    return "medium";
  } else {
    return "low";
  }
};

/**
 * Calculates potential revenue opportunity based on client value and missed leads
 * @param {Object} formData - The form data
 * @returns {Object} - Revenue opportunity analysis
 */
const calculateRevenueOpportunity = (formData) => {
  const valueMap = {
    "200-500": 350,
    "500-1000": 750,
    "1000-2000": 1500,
    "1000+": 2000, // FIX: Add missing 1000+ option
    "depends-on-job": 600,
  };

  const costMap = {
    "50-100": 75,
    "100-300": 200,
    "300-1000": 650,
    "no-idea": 300,
  };

  const weeklyValue = valueMap[formData.extraClientValue] || 0;
  const leadCost = costMap[formData.missedLeadCost] || 0;

  return {
    weekly_client_value: weeklyValue,
    monthly_client_value: weeklyValue * 4,
    annual_client_value: weeklyValue * 52,
    missed_lead_cost: leadCost,
    monthly_missed_opportunity: leadCost * 4,
    annual_missed_opportunity: leadCost * 48,
    automation_roi_potential: weeklyValue > 0 ? (weeklyValue * 52) / 12 : 0, // Monthly ROI potential
  };
};

/**
 * Calculates business maturity assessment
 * @param {Object} formData - The form data
 * @returns {Object} - Business maturity analysis
 */
const calculateBusinessMaturity = (formData) => {
  let maturityScore = 0;
  let maturityFactors = [];

  // Years in business scoring
  const yearsScore = {
    "Less than 1 year": 1,
    "1-2 years": 2,
    "3-5 years": 3,
    "6-10 years": 4,
    "11-20 years": 5,
    "More than 20 years": 5,
  };
  const yearsValue = yearsScore[formData.yearsInBusiness] || 1;
  maturityScore += yearsValue * 20;

  // Employee count scoring
  const employeeScore = {
    "Just me (solo)": 1,
    "2-5 employees": 2,
    "6-10 employees": 3,
    "11-25 employees": 4,
    "26-50 employees": 5,
    "More than 50 employees": 5,
  };
  const empValue = employeeScore[formData.employeeCount] || 1;
  maturityScore += empValue * 15;

  // Lead source diversification
  const leadCount = (formData.leadSources || []).length;
  maturityScore += Math.min(20, leadCount * 5);

  // Team structure sophistication
  const teamScore = {
    solo: 5,
    "no-owner": 0,
    "small-team": 15,
    "sales-team": 20,
  };
  maturityScore += teamScore[formData.teamStructure] || 0;

  // Determine maturity level
  let level = "Startup";
  if (maturityScore >= 80) level = "Mature";
  else if (maturityScore >= 60) level = "Growing";
  else if (maturityScore >= 40) level = "Developing";

  return {
    score: Math.min(100, maturityScore),
    level: level,
    years_weight: yearsValue,
    team_weight: empValue,
    diversification_score: leadCount,
    strengths: maturityFactors,
  };
};

/**
 * Calculates technology adoption score
 * @param {Object} formData - The form data
 * @returns {Object} - Technology adoption analysis
 */
const calculateTechnologyAdoption = (formData) => {
  let adoptionScore = 0;
  const digitalSources = [
    "website-form",
    "facebook-ads",
    "google-ads",
    "instagram-dms",
  ];
  const traditionalSources = ["phone-calls", "walk-ins-referrals"];

  const leadSources = formData.leadSources || [];

  // Digital vs traditional ratio
  const digitalCount = leadSources.filter((source) =>
    digitalSources.includes(source)
  ).length;
  const traditionalCount = leadSources.filter((source) =>
    traditionalSources.includes(source)
  ).length;

  adoptionScore += digitalCount * 15; // Up to 60 points for digital
  adoptionScore += traditionalCount * 5; // Up to 10 points for traditional

  // Current tools analysis
  const currentTools = (formData.currentTools || "").toLowerCase();
  const techIndicators = [
    "crm",
    "software",
    "app",
    "digital",
    "online",
    "cloud",
    "automation",
  ];
  const toolMatches = techIndicators.filter((indicator) =>
    currentTools.includes(indicator)
  ).length;
  adoptionScore += toolMatches * 5;

  // Automation readiness
  if (formData.leadResponse === "automation") adoptionScore += 20;

  let adoptionLevel = "Low";
  if (adoptionScore >= 70) adoptionLevel = "High";
  else if (adoptionScore >= 40) adoptionLevel = "Medium";

  return {
    score: Math.min(100, adoptionScore),
    level: adoptionLevel,
    digital_channel_count: digitalCount,
    traditional_channel_count: traditionalCount,
    tech_tool_indicators: toolMatches,
    digital_ratio:
      leadSources.length > 0 ? (digitalCount / leadSources.length) * 100 : 0,
  };
};

/**
 * Calculates pain point severity analysis
 * @param {Object} formData - The form data
 * @returns {Object} - Pain point severity analysis
 */
const calculatePainPointSeverity = (formData) => {
  const painPoints = {
    time_management: 0,
    lead_response: 0,
    missed_opportunities: 0,
    growth_challenges: 0,
    system_inefficiency: 0,
  };

  // Time management pain
  const timeTaskSeverity = {
    "chasing-leads": 80,
    "admin-paperwork": 70,
    "managing-communication": 75,
    "quotes-estimates": 60,
    "scheduling-coordination": 65,
    "marketing-social": 50,
    "inventory-procurement": 40,
  };
  painPoints.time_management =
    timeTaskSeverity[formData.timeConsumingTasks] || 30;

  // Lead response pain
  const responseSeverity = {
    immediate: 10,
    "few-hours": 30,
    "day-or-more": 70,
    "forget-unless-message": 90,
    automation: 5,
  };
  painPoints.lead_response = responseSeverity[formData.leadResponse] || 50;

  // Missed opportunities pain
  const missedSeverity = {
    rarely: 20,
    sometimes: 60,
    often: 90,
    "dont-know": 70,
    "not-really": 15,
    "yes-costs-us": 85,
    "not-sure": 65,
  };
  painPoints.missed_opportunities =
    missedSeverity[formData.missedOpportunities] || 40;

  // Growth challenge severity
  const growthSeverity = {
    "getting-more-leads": 70,
    "converting-more-leads": 80,
    "too-many-no-shows": 75,
    "time-management": 85,
    "staying-organized": 70,
    "no-systems-in-place": 90,
    "scaling-the-team": 65,
    "customer-retention": 60,
  };
  painPoints.growth_challenges =
    growthSeverity[formData.biggestGrowthChallenge] || 50;

  // System inefficiency
  if (formData.teamStructure === "no-owner")
    painPoints.system_inefficiency += 40;
  if (!formData.currentTools || formData.currentTools.length < 10)
    painPoints.system_inefficiency += 30;

  const averagePain =
    Object.values(painPoints).reduce((a, b) => a + b, 0) /
    Object.keys(painPoints).length;

  let severityLevel = "Low";
  if (averagePain >= 70) severityLevel = "Critical";
  else if (averagePain >= 50) severityLevel = "High";
  else if (averagePain >= 30) severityLevel = "Medium";

  return {
    overall_severity: Math.round(averagePain),
    severity_level: severityLevel,
    pain_points: painPoints,
    most_critical_area: Object.keys(painPoints).reduce((a, b) =>
      painPoints[a] > painPoints[b] ? a : b
    ),
    pain_point_count: Object.values(painPoints).filter((score) => score > 60)
      .length,
  };
};

/**
 * Enhanced automation readiness score
 * @param {Object} formData - The form data
 * @returns {Object} - Automation readiness analysis
 */
const calculateAutomationReadiness = (formData) => {
  let readinessScore = 0;

  // Goals clarity (primary + secondary goals)
  const primaryGoalScore = formData.primaryGoal ? 15 : 0;
  const secondaryGoalsCount = (formData.secondaryGoals || []).length;
  const goalsCount = (formData.goals || []).length; // Backward compatibility
  const totalGoalsScore =
    primaryGoalScore +
    Math.min(10, secondaryGoalsCount * 2) +
    Math.min(10, goalsCount * 2);
  readinessScore += totalGoalsScore;

  // Current automation preferences
  const automationCount = (formData.automationPreferences || []).length;
  readinessScore += Math.min(20, automationCount * 3);

  // Business stability (years + employees)
  const businessStability = calculateBusinessMaturity(formData).score * 0.2;
  readinessScore += businessStability;

  // Technology adoption
  const techAdoption = calculateTechnologyAdoption(formData).score * 0.15;
  readinessScore += techAdoption;

  // Pain point urgency
  const painLevel = calculatePainPointSeverity(formData).overall_severity * 0.2;
  readinessScore += painLevel;

  let readinessLevel = "Not Ready";
  if (readinessScore >= 75) readinessLevel = "Highly Ready";
  else if (readinessScore >= 55) readinessLevel = "Ready";
  else if (readinessScore >= 35) readinessLevel = "Somewhat Ready";

  return {
    score: Math.min(100, Math.round(readinessScore)),
    level: readinessLevel,
    goals_clarity_score: Math.round(totalGoalsScore), // FIX: Use calculated total
    automation_interest_score: automationCount * 3,
    business_stability_component: Math.round(businessStability),
    tech_adoption_component: Math.round(techAdoption),
    urgency_component: Math.round(painLevel),
  };
};

/**
 * Calculates customer journey complexity
 * @param {Object} formData - The form data
 * @returns {Object} - Customer journey complexity analysis
 */
const calculateCustomerJourneyComplexity = (formData) => {
  let complexityScore = 0;

  // Lead source count increases complexity
  const leadSourceCount = (formData.leadSources || []).length;
  complexityScore += leadSourceCount * 10;

  // Mixed digital/traditional increases complexity
  const digitalSources = [
    "website-form",
    "facebook-ads",
    "google-ads",
    "instagram-dms",
  ];
  const leadSources = formData.leadSources || [];
  const hasDigital = leadSources.some((source) =>
    digitalSources.includes(source)
  );
  const hasTraditional = leadSources.some(
    (source) => !digitalSources.includes(source)
  );
  if (hasDigital && hasTraditional) complexityScore += 15;

  // Team structure complexity
  const teamComplexity = {
    solo: 10,
    "no-owner": 30,
    "small-team": 20,
    "sales-team": 25,
  };
  complexityScore += teamComplexity[formData.teamStructure] || 15;

  // Response time variability
  if (
    formData.leadResponse === "day-or-more" ||
    formData.leadResponse === "forget-unless-message"
  ) {
    complexityScore += 20;
  }

  let complexityLevel = "Simple";
  if (complexityScore >= 60) complexityLevel = "Complex";
  else if (complexityScore >= 40) complexityLevel = "Moderate";

  return {
    score: Math.min(100, complexityScore),
    level: complexityLevel,
    lead_source_diversity: leadSourceCount,
    channel_mix_complexity: hasDigital && hasTraditional ? "Mixed" : "Unified",
    team_coordination_needs: teamComplexity[formData.teamStructure] || 15,
  };
};

/**
 * Calculates growth potential score
 * @param {Object} formData - The form data
 * @returns {Object} - Growth potential analysis
 */
const calculateGrowthPotential = (formData) => {
  let growthScore = 0;

  // Revenue opportunity from existing calculations
  const revenueOpp = calculateRevenueOpportunity(formData);
  if (revenueOpp.annual_client_value > 0) {
    growthScore += Math.min(30, (revenueOpp.annual_client_value / 1000) * 2);
  }

  // Business maturity enables growth
  const maturity = calculateBusinessMaturity(formData);
  growthScore += maturity.score * 0.25;

  // Goals indicate growth ambition
  const growthGoals = [
    "increase-leads",
    "increase-revenue",
    "scale-business",
    "competitive-advantage",
  ];

  // Check primary goal
  const primaryGoalIsGrowth = growthGoals.includes(formData.primaryGoal);
  if (primaryGoalIsGrowth) growthScore += 25;

  // Check secondary goals
  const secondaryGoals = formData.secondaryGoals || [];
  const hasSecondaryGrowthGoals = secondaryGoals.some((goal) =>
    growthGoals.includes(goal)
  );
  if (hasSecondaryGrowthGoals) growthScore += 15;

  // Backward compatibility
  const legacyGoals = formData.goals || [];
  const hasLegacyGrowthGoals = legacyGoals.some((goal) =>
    growthGoals.includes(goal)
  );
  if (hasLegacyGrowthGoals) growthScore += 10;

  growthScore += secondaryGoals.length * 2 + legacyGoals.length * 1;

  // Market position based on lead sources
  const leadDiversity = (formData.leadSources || []).length;
  growthScore += Math.min(15, leadDiversity * 3);

  let potentialLevel = "Limited";
  if (growthScore >= 70) potentialLevel = "High";
  else if (growthScore >= 45) potentialLevel = "Moderate";

  return {
    score: Math.min(100, Math.round(growthScore)),
    level: potentialLevel,
    revenue_potential: revenueOpp.annual_client_value,
    maturity_factor: Math.round(maturity.score * 0.25),
    ambition_indicator:
      primaryGoalIsGrowth || hasSecondaryGrowthGoals || hasLegacyGrowthGoals,
    market_reach_score: leadDiversity * 3,
  };
};

/**
 * Calculates risk assessment
 * @param {Object} formData - The form data
 * @returns {Object} - Risk assessment analysis
 */
const calculateRiskAssessment = (formData) => {
  const risks = {
    revenue_loss_risk: 0,
    competitive_risk: 0,
    operational_risk: 0,
    growth_stagnation_risk: 0,
  };

  // Revenue loss risk
  const revenueOpp = calculateRevenueOpportunity(formData);
  risks.revenue_loss_risk = Math.min(
    90,
    (revenueOpp.annual_missed_opportunity / 1000) * 10
  );

  // Competitive risk
  const techAdoption = calculateTechnologyAdoption(formData);
  if (techAdoption.score < 40) risks.competitive_risk = 70;
  else if (techAdoption.score < 60) risks.competitive_risk = 45;
  else risks.competitive_risk = 20;

  // Operational risk
  const painPoints = calculatePainPointSeverity(formData);
  risks.operational_risk = painPoints.overall_severity;

  // Growth stagnation risk
  const growthPotential = calculateGrowthPotential(formData);
  risks.growth_stagnation_risk = 100 - growthPotential.score;

  const averageRisk =
    Object.values(risks).reduce((a, b) => a + b, 0) / Object.keys(risks).length;

  let riskLevel = "Low";
  if (averageRisk >= 70) riskLevel = "Critical";
  else if (averageRisk >= 50) riskLevel = "High";
  else if (averageRisk >= 30) riskLevel = "Medium";

  return {
    overall_risk_score: Math.round(averageRisk),
    risk_level: riskLevel,
    risk_breakdown: risks,
    highest_risk_area: Object.keys(risks).reduce((a, b) =>
      risks[a] > risks[b] ? a : b
    ),
    annual_potential_loss: revenueOpp.annual_missed_opportunity,
  };
};

/**
 * Calculates digital transformation readiness
 * @param {Object} formData - The form data
 * @returns {Object} - Digital readiness analysis
 */
const calculateDigitalReadiness = (formData) => {
  let digitalScore = 0;

  // Technology adoption score
  const techAdoption = calculateTechnologyAdoption(formData);
  digitalScore += techAdoption.score * 0.4;

  // Automation interest
  const automationCount = (formData.automationPreferences || []).length;
  digitalScore += Math.min(25, automationCount * 4);

  // Digital-first goals
  const digitalGoals = [
    "improve-efficiency",
    "competitive-advantage",
    "scale-business",
  ];

  // Check primary and secondary goals for digital focus
  const primaryIsDigital = digitalGoals.includes(formData.primaryGoal);
  const secondaryHasDigital = (formData.secondaryGoals || []).some((goal) =>
    digitalGoals.includes(goal)
  );
  const legacyHasDigital = (formData.goals || []).some((goal) =>
    digitalGoals.includes(goal)
  );

  if (primaryIsDigital) digitalScore += 20;
  else if (secondaryHasDigital || legacyHasDigital) digitalScore += 10;

  // Business maturity (stable businesses transform easier)
  const maturity = calculateBusinessMaturity(formData);
  digitalScore += maturity.score * 0.2;

  let readinessLevel = "Not Ready";
  if (digitalScore >= 75) readinessLevel = "Highly Ready";
  else if (digitalScore >= 55) readinessLevel = "Ready";
  else if (digitalScore >= 35) readinessLevel = "Somewhat Ready";

  return {
    score: Math.min(100, Math.round(digitalScore)),
    level: readinessLevel,
    technology_foundation: techAdoption.score,
    automation_appetite: automationCount,
    digital_mindset:
      primaryIsDigital || secondaryHasDigital || legacyHasDigital,
    organizational_readiness: maturity.score,
  };
};

/**
 * Analyzes lead sources
 * @param {Object} formData - The form data
 * @returns {Object} - Lead source analysis
 */
const analyzeLeadSources = (formData) => {
  const leadSources = formData.leadSources || [];
  const digitalSources = [
    "website-form",
    "facebook-ads",
    "google-ads",
    "instagram-dms",
    "whatsapp",
  ];
  const traditionalSources = ["phone-calls", "walk-ins-referrals"];

  const digitalCount = leadSources.filter((source) =>
    digitalSources.includes(source)
  ).length;
  const traditionalCount = leadSources.filter((source) =>
    traditionalSources.includes(source)
  ).length;

  let diversificationScore = leadSources.length * 20;
  let riskScore = 100 - diversificationScore; // More sources = less risk

  let strategy = "Diversified";
  if (digitalCount === 0) strategy = "Traditional Only";
  else if (traditionalCount === 0) strategy = "Digital Only";
  else if (digitalCount > traditionalCount) strategy = "Digital Heavy";
  else strategy = "Traditional Heavy";

  return {
    total_sources: leadSources.length,
    digital_sources: digitalCount,
    traditional_sources: traditionalCount,
    diversification_score: Math.min(100, diversificationScore),
    single_source_risk: riskScore,
    strategy_type: strategy,
    digital_ratio:
      leadSources.length > 0
        ? Math.round((digitalCount / leadSources.length) * 100)
        : 0,
    sources_breakdown: leadSources,
  };
};

/**
 * Analyzes team efficiency
 * @param {Object} formData - The form data
 * @returns {Object} - Team efficiency analysis
 */
const analyzeTeamEfficiency = (formData) => {
  let efficiencyScore = 0;

  // Team structure efficiency
  const structureEfficiency = {
    solo: 60, // High autonomy but limited capacity
    "small-team": 75, // Good balance
    "sales-team": 85, // Specialized roles
    "no-owner": 25, // No clear responsibility
  };
  efficiencyScore = structureEfficiency[formData.teamStructure] || 50;

  // Response time efficiency
  const responseEfficiency = {
    immediate: 95,
    "few-hours": 80,
    "day-or-more": 45,
    "forget-unless-message": 15,
    automation: 100,
  };
  const responseScore = responseEfficiency[formData.leadResponse] || 50;
  efficiencyScore = (efficiencyScore + responseScore) / 2;

  // Missed opportunities impact
  const missedImpact = {
    rarely: 5,
    sometimes: 25,
    often: 60,
    "dont-know": 40,
    "not-really": 5,
    "yes-costs-us": 70,
    "not-sure": 35,
  };
  const missedScore = missedImpact[formData.missedOpportunities] || 30;
  efficiencyScore = efficiencyScore - missedScore * 0.5;

  let efficiencyLevel = "Poor";
  if (efficiencyScore >= 80) efficiencyLevel = "Excellent";
  else if (efficiencyScore >= 65) efficiencyLevel = "Good";
  else if (efficiencyScore >= 45) efficiencyLevel = "Average";

  return {
    score: Math.max(0, Math.round(efficiencyScore)),
    level: efficiencyLevel,
    team_structure_score: structureEfficiency[formData.teamStructure] || 50,
    response_time_score: responseScore,
    missed_opportunity_penalty: Math.round(missedScore * 0.5),
    bottleneck_indicators:
      formData.teamStructure === "no-owner"
        ? ["No ownership", "Unclear responsibility"]
        : [],
  };
};

/**
 * Calculates comprehensive revenue leak analysis
 * @param {Object} formData - The form data
 * @returns {Object} - Revenue leak analysis
 */
const calculateRevenueLeak = (formData) => {
  const revenueOpp = getMemoizedCalculation(
    "revenue_opportunity",
    calculateRevenueOpportunity,
    formData
  );
  const teamEfficiency = getMemoizedCalculation(
    "team_efficiency",
    analyzeTeamEfficiency,
    formData
  );

  // Calculate different types of revenue leaks
  const leaks = {
    missed_leads: revenueOpp.annual_missed_opportunity,
    delayed_response: 0,
    poor_follow_up: 0,
    system_inefficiency: 0,
  };

  // Delayed response leak
  if (formData.leadResponse === "day-or-more") {
    leaks.delayed_response = revenueOpp.annual_client_value * 0.15; // 15% loss
  } else if (formData.leadResponse === "forget-unless-message") {
    leaks.delayed_response = revenueOpp.annual_client_value * 0.3; // 30% loss
  }

  // Poor follow-up leak
  if (
    formData.leadSourceFollowUp === "sometimes-forget" ||
    formData.leadSourceFollowUp === "dont-know"
  ) {
    leaks.poor_follow_up = revenueOpp.annual_client_value * 0.2;
  }

  // System inefficiency leak
  if (teamEfficiency.score < 50) {
    leaks.system_inefficiency = revenueOpp.annual_client_value * 0.1;
  }

  const totalLeak = Object.values(leaks).reduce((a, b) => a + b, 0);
  const leakPercentage =
    revenueOpp.annual_client_value > 0
      ? (totalLeak / revenueOpp.annual_client_value) * 100
      : 0;

  return {
    total_annual_leak: Math.round(totalLeak),
    leak_percentage: Math.round(leakPercentage),
    leak_breakdown: leaks,
    biggest_leak_source: Object.keys(leaks).reduce((a, b) =>
      leaks[a] > leaks[b] ? a : b
    ),
    monthly_leak: Math.round(totalLeak / 12),
    potential_recovery: Math.round(totalLeak * 0.8), // 80% of leaks could be recovered with automation
  };
};

/**
 * Generates implementation recommendations
 * @param {Object} formData - The form data
 * @returns {Object} - Implementation recommendations
 */
const generateImplementationRecommendations = (formData) => {
  const urgency = getMemoizedCalculation(
    "urgency_level",
    calculateUrgencyLevel,
    formData
  );
  const readiness = getMemoizedCalculation(
    "automation_readiness",
    calculateAutomationReadiness,
    formData
  );
  const painPoints = getMemoizedCalculation(
    "pain_points",
    calculatePainPointSeverity,
    formData
  );
  const revenueLeak = getMemoizedCalculation(
    "revenue_leak",
    calculateRevenueLeak,
    formData
  );

  let priority = "Medium";
  let phase = "Gradual";

  // Determine timeline based on urgency and revenue leak
  let timeline = "6-12 months"; // Default timeline
  if (urgency === "high" || revenueLeak.total_annual_leak > 50000) {
    timeline = "1-3 months";
    priority = "High";
    phase = "Aggressive";
  } else if (urgency === "medium" || revenueLeak.total_annual_leak > 20000) {
    timeline = "3-6 months";
    priority = "Medium-High";
    phase = "Structured";
  }

  // Adjust based on readiness
  if (readiness.score < 40) {
    timeline = "6-12 months";
    phase = "Foundation First";
  }

  // Recommended starting points
  const startingPoints = [];
  if (painPoints.pain_points.lead_response > 60)
    startingPoints.push("Lead Response Automation");
  if (painPoints.pain_points.time_management > 70)
    startingPoints.push("Administrative Automation");
  if (painPoints.pain_points.missed_opportunities > 65)
    startingPoints.push("Follow-up Automation");

  return {
    implementation_priority: priority,
    phase_approach: phase,
    timeline: timeline,
    starting_points: startingPoints,
    readiness_score: readiness.score,
    urgency_indicators: urgency,
  };
};

/**
 * Calculates competitive position analysis
 * @param {Object} formData - The form data
 * @returns {Object} - Competitive analysis
 */
const calculateCompetitivePosition = (formData) => {
  const techAdoption = getMemoizedCalculation(
    "tech_adoption",
    calculateTechnologyAdoption,
    formData
  );
  const digitalReadiness = getMemoizedCalculation(
    "digital_readiness",
    calculateDigitalReadiness,
    formData
  );
  const responseTime = formData.leadResponse;

  let competitiveScore = 0;

  // Technology advantage
  competitiveScore += techAdoption.score * 0.3;

  // Response speed advantage
  const responseAdvantage = {
    immediate: 90,
    automation: 100,
    "few-hours": 70,
    "day-or-more": 30,
    "forget-unless-message": 10,
  };
  competitiveScore += (responseAdvantage[responseTime] || 50) * 0.3;

  // Digital presence
  competitiveScore += digitalReadiness.score * 0.25;

  // Lead source sophistication
  const leadSources = formData.leadSources || [];
  const modernSources = ["website-form", "facebook-ads", "google-ads"];
  const hasModernSources = leadSources.some((source) =>
    modernSources.includes(source)
  );
  if (hasModernSources) competitiveScore += 15;

  let position = "Behind";
  if (competitiveScore >= 75) position = "Leading";
  else if (competitiveScore >= 55) position = "Competitive";
  else if (competitiveScore >= 35) position = "Average";

  return {
    score: Math.round(competitiveScore),
    position: position,
    technology_advantage: techAdoption.score,
    response_speed_advantage: responseAdvantage[responseTime] || 50,
    digital_presence_score: digitalReadiness.score,
    modernization_needs:
      competitiveScore < 50
        ? [
            "Digital transformation",
            "Automation implementation",
            "Response optimization",
          ]
        : [],
  };
};
